/**
 * DebtPayoffAnalyzer: payoff with and without an extra monthly payment, and avalanche /
 * snowball ranking (CONVENTIONS §5.7).
 */
import { addMonths, type Day } from '../core/calendar.ts';
import { add, compare, fromInt, gt, lte, max, min, mul, round2, sub, ZERO, type Decimal } from '../core/decimal.ts';
import type { Transaction } from '../types/domain.ts';
import { debtMonthlyRate, isActiveDebt, MAX_PAYOFF_MONTHS, remainingBalance } from './debt-projector.ts';

export type PayoffStrategy = 'avalanche' | 'snowball';

export interface PayoffAnalysis {
  readonly debt: Transaction;
  readonly currentBalance: Decimal;
  readonly interestRate: Decimal;
  readonly currentMonthlyPayment: Decimal;
  readonly currentPayoffDate: Day;
  readonly currentTotalInterest: Decimal;
  readonly currentMonthsRemaining: number;
  readonly acceleratedPayoffDate: Day;
  readonly acceleratedTotalInterest: Decimal;
  readonly acceleratedMonthsRemaining: number;
  readonly monthsSaved: number;
  readonly interestSaved: Decimal;
  /** Double. */
  readonly yearsSaved: number;
}

export interface RankedDebt {
  readonly debt: Transaction;
  readonly rank: number;
  readonly currentBalance: Decimal;
  readonly interestRate: Decimal;
  readonly monthlyPayment: Decimal;
  readonly analysis: PayoffAnalysis | null;
}

export interface AcceleratorResult {
  readonly rankedDebts: readonly RankedDebt[];
  readonly strategy: PayoffStrategy;
  readonly extraMonthlyPayment: Decimal;
  readonly recommendedTargetDebt: Transaction | null;
  readonly totalCurrentDebt: Decimal;
  readonly totalInterestSaved: Decimal;
  readonly totalMonthsSaved: number;
  /** Double. */
  readonly totalYearsSaved: number;
  readonly newDebtFreeDate: Day | null;
  readonly currentDebtFreeDate: Day | null;
}

interface PayoffDetails {
  readonly months: number;
  readonly interest: Decimal;
  readonly payoffDate: Day;
}

/**
 * When the payment does not cover a positive rate's interest, the cap (or 600) months of flat,
 * unrounded interest; otherwise the month-by-month run, stopping at `maxPayments`.
 */
function payoffDetails(balance: Decimal, rate: Decimal, payment: Decimal, start: Day, maxPayments: number | null): PayoffDetails {
  if (lte(payment, mul(balance, rate)) && gt(rate, ZERO)) {
    const months = maxPayments ?? MAX_PAYOFF_MONTHS;
    return { months, interest: mul(mul(balance, rate), fromInt(months)), payoffDate: addMonths(start, months) };
  }
  let remaining = balance;
  let interest: Decimal = ZERO;
  let months = 0;
  while (gt(remaining, ZERO) && months < MAX_PAYOFF_MONTHS) {
    if (maxPayments !== null && months >= maxPayments) break;
    if (months > 0) {
      const accrued = mul(remaining, rate);
      interest = add(interest, accrued);
      remaining = add(remaining, accrued);
    }
    remaining = max(sub(remaining, min(payment, remaining)), ZERO);
    months += 1;
  }
  return { months, interest: round2(interest), payoffDate: addMonths(start, months) };
}

/** Payoff with the debt's own payment (capped by its term) and with `extra` added (uncapped). */
export function analyzeDebt(debt: Transaction, extra: Decimal, date: Day, now: Day): PayoffAnalysis | null {
  const balance = remainingBalance(debt, date, now);
  if (!gt(balance, ZERO)) return null;

  const rate = debtMonthlyRate(debt);
  const current = payoffDetails(balance, rate, debt.amount, date, debt.endAfterOccurrences ?? debt.debtTermMonths);
  const accelerated = payoffDetails(balance, rate, add(debt.amount, extra), date, null);
  const monthsSaved = current.months - accelerated.months;
  return {
    debt,
    currentBalance: balance,
    interestRate: debt.interestRate ?? ZERO,
    currentMonthlyPayment: debt.amount,
    currentPayoffDate: current.payoffDate,
    currentTotalInterest: current.interest,
    currentMonthsRemaining: current.months,
    acceleratedPayoffDate: accelerated.payoffDate,
    acceleratedTotalInterest: accelerated.interest,
    acceleratedMonthsRemaining: accelerated.months,
    monthsSaved,
    interestSaved: sub(current.interest, accelerated.interest),
    yearsSaved: monthsSaved / 12,
  };
}

/** Avalanche: highest rate first; snowball: smallest remaining balance first. Stable; no filtering. */
export function rankDebts(debts: readonly Transaction[], strategy: PayoffStrategy, date: Day, now: Day): readonly Transaction[] {
  const keyed = debts.map((debt) => ({
    debt,
    key: strategy === 'avalanche' ? debt.interestRate ?? ZERO : remainingBalance(debt, date, now),
  }));
  const direction = strategy === 'avalanche' ? -1 : 1;
  return [...keyed].sort((a, b) => direction * compare(a.key, b.key)).map((entry) => entry.debt);
}

export const recommendTargetDebt = (
  debts: readonly Transaction[],
  strategy: PayoffStrategy,
  date: Day,
  now: Day,
): Transaction | null => rankDebts(debts.filter(isActiveDebt), strategy, date, now)[0] ?? null;

const laterOf = (latest: Day, candidate: Day): Day => (candidate > latest ? candidate : latest);

/**
 * Rank the active debts and give the whole extra payment to the first; savings come from
 * debts sharing the target's id, debt-free dates are the latest payoffs (starting from `date`).
 */
export function analyzePayoff(
  debts: readonly Transaction[],
  extra: Decimal,
  strategy: PayoffStrategy,
  date: Day,
  now: Day,
): AcceleratorResult {
  const active = debts.filter(isActiveDebt);
  const ranked = rankDebts(active, strategy, date, now);
  const target = ranked[0] ?? null;
  const rankedDebts = ranked.map((debt, index): RankedDebt => ({
    debt,
    rank: index + 1,
    currentBalance: remainingBalance(debt, date, now),
    interestRate: debt.interestRate ?? ZERO,
    monthlyPayment: debt.amount,
    analysis: analyzeDebt(debt, debt.id === target?.id ? extra : ZERO, date, now),
  }));
  const targeted = rankedDebts.filter((r) => r.debt.id === target?.id && r.analysis !== null).map((r) => r.analysis!);
  const analyses = rankedDebts.flatMap((r) => (r.analysis === null ? [] : [r.analysis]));
  const totalMonthsSaved = targeted.reduce((total, a) => total + a.monthsSaved, 0);
  const hasDebts = active.length > 0;
  return {
    rankedDebts,
    strategy,
    extraMonthlyPayment: extra,
    recommendedTargetDebt: target,
    totalCurrentDebt: rankedDebts.reduce<Decimal>((total, r) => add(total, r.currentBalance), ZERO),
    totalInterestSaved: targeted.reduce<Decimal>((total, a) => add(total, a.interestSaved), ZERO),
    totalMonthsSaved,
    totalYearsSaved: totalMonthsSaved / 12,
    newDebtFreeDate: hasDebts ? analyses.reduce((latest, a) => laterOf(latest, a.acceleratedPayoffDate), date) : null,
    currentDebtFreeDate: hasDebts ? analyses.reduce((latest, a) => laterOf(latest, a.currentPayoffDate), date) : null,
  };
}
